import { UTM_SESSION_STORAGE_KEY } from "@lightspeed-payments/payments-portal-sdk/dist/tracking/utm";
import { decamelizeKeys } from "humps";
import { either, equals, merge } from "ramda";
import { HQ_SERVICE, LOGIN_ROUTE, X_UPSERVE_AUTH_TOKEN } from "app_constants";
import { getCookie } from "helpers/cookies";
import { reportError } from "helpers/error_reporting/error_reporting";
import { sleep } from "helpers/sleep";

import { RequestStatus } from "types/request";

function postRequest(values: any = {}, headers: any = {}): RequestInit {
  return {
    method: "POST",
    credentials: "include",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...headers,
    },
    body: JSON.stringify(decamelizeKeys(values)),
  };
}

function putRequest(values: any = {}, headers: any = {}): RequestInit {
  return {
    method: "PUT",
    credentials: "include",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...headers,
    },
    body: JSON.stringify(decamelizeKeys(values)),
  };
}

function deleteRequest(headers: any = {}): RequestInit {
  return {
    method: "DELETE",
    credentials: "include",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...headers,
    },
  };
}

function getRequest(headers: any = {}): RequestInit {
  return {
    method: "GET",
    credentials: "include",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...headers,
    },
  };
}

/**
 * It checks if the LSPay email tracking parameters through
 * `@lightspeed-payments/payments-portal-sdk/dist/tracking/utm`
 * are set properly.
 * It sets the session storage with a string even if there is no value.
 *
 * If it exists, it resolves immediately.
 * If it doesn't exist, it does a maximum 5 attempts following a 0.1 second wait.
 * It will resolve after the max attempts.
 */
async function sessionStoragePopulatedOrTimeout() {
  let attempt = 0;
  while (
    sessionStorage[UTM_SESSION_STORAGE_KEY] === undefined &&
    attempt++ < 5
  ) {
    // Give up the thread and wait 0.1 seconds
    await sleep(100);
  }
}

async function checkStatus(response: any) {
  if (response.status === 401) {
    // Give a chance for LSPay email tracking parameters to populate before redirect
    await sessionStoragePopulatedOrTimeout();
    window.location.href = `${HQ_SERVICE}${LOGIN_ROUTE}?prompt=none&return_to=${encodeURIComponent(
      window.location.origin
    )}`;
  } else if (response.status >= 200 && response.status < 300) {
    return response;
  }

  const error = new Error(response.statusText);

  const requestId = response.headers.get("X-Request-Id");
  reportError(error, requestId ? { requestId } : undefined);

  throw error;
}

function makeAuthHeaders() {
  const authToken = getCookie(X_UPSERVE_AUTH_TOKEN) || "";
  return { "X-Auth-Token": authToken };
}

const parseJSON = (response: any) => response.json();

const postWithoutCredentials = (params: object) => {
  return merge(postRequest(params), { credentials: "omit" });
};

const isRequestNotStarted = equals(RequestStatus.NOT_STARTED);
const isRequestRequesting = equals(RequestStatus.REQUESTING);
const isRequestSuccess = equals(RequestStatus.SUCCESS);
const isRequestFailure = equals(RequestStatus.FAILURE);
const isRequestComplete = either(isRequestSuccess, isRequestFailure);

export {
  checkStatus,
  deleteRequest,
  isRequestComplete,
  isRequestFailure,
  isRequestNotStarted,
  isRequestRequesting,
  isRequestSuccess,
  getRequest,
  makeAuthHeaders,
  parseJSON,
  postRequest,
  postWithoutCredentials,
  putRequest,
};
